export const queryKeysExtended = {
  organization: {
    current: ['organization'] as const,
    membership: (userId?: string) => ['organization-membership', userId] as const,
    members: (orgId?: string) => ['organization-members', orgId] as const,
    people: (userId?: string) => ['org-people', userId] as const,
    invites: (orgId?: string) => ['organization-invites', orgId] as const,
    inviteByToken: (token?: string) => ['organization-invite-token', token] as const,
    sensitiveDataAlertsBase: ['sensitive-data-alerts'] as const,
    sensitiveDataAlerts: (orgId?: string, options?: Record<string, unknown>) => ['sensitive-data-alerts', orgId, options] as const,
    sensitiveDataAlertCounts: (orgId?: string) => ['sensitive-data-alert-counts', orgId] as const,
    settings: ['organization-settings'] as const,
    meetingPrepSettings: (orgId?: string) => ['meeting-prep-settings', orgId] as const,
    meetingAutoSettings: (orgId?: string) => ['meeting-auto-settings', orgId] as const,
  },

  imports: {
    pending: ['pending-imports'] as const,
    pendingWithStatus: (status?: string) => ['pending-imports', status] as const,
    pendingCount: ['pending-imports-count'] as const,
  },

  driveDiscovery: {
    all: ['drive-discovery'] as const,
    withDateRange: (dateFrom?: string, dateTo?: string) =>
      ['drive-discovery', dateFrom, dateTo] as const,
  },

  compliance: {
    obligations: ['compliance-obligations'] as const,
    data: ['compliance-data'] as const,
  },

  obligationDiscussions: {
    all: ['obligation-discussions'] as const,
    byObligation: (obligationId?: string) => ['obligation-discussions', 'by-obligation', obligationId] as const,
    byAnalysis: (analysisId?: string) => ['obligation-discussions', 'by-analysis', analysisId] as const,
    byClient: (clientId?: string) => ['obligation-discussions', 'by-client', clientId] as const,
  },

  gamma: {
    status: (generationId?: string | null) => ['gamma-status', generationId] as const,
    themes: ['gamma-themes'] as const,
  },

  everhour: {
    all: ['everhour'] as const,
    connection: (orgId?: string) => ['everhour-connection', orgId] as const,
    projects: (orgId?: string) => ['everhour-projects', orgId] as const,
    users: (orgId?: string) => ['everhour-users', orgId] as const,
    timeEntriesBase: ['time-entries'] as const,
    timeEntries: (orgId?: string, from?: string, to?: string, clientId?: string) =>
      ['time-entries', orgId, from, to, clientId] as const,
    timeSummaryBase: ['time-summary'] as const,
    timeSummary: (orgId?: string, from?: string, to?: string, clientId?: string) =>
      ['time-summary', orgId, from, to, clientId] as const,
    clientTime: (orgId?: string, clientId?: string) => ['client-time', orgId, clientId] as const,
    syncStatus: (orgId?: string) => ['everhour-sync-status', orgId] as const,
    weeklyBreakdown: (orgId?: string, weekOffset?: number) => ['weekly-time-breakdown', orgId, weekOffset] as const,
    monthlyBreakdownBase: ['monthly-time-breakdown'] as const,
    monthlyBreakdown: (orgId?: string, monthStr?: string) => ['monthly-time-breakdown', orgId, monthStr] as const,
    unmappedProjects: (orgId?: string, weekOffset?: number) => ['unmapped-everhour-projects', orgId, weekOffset] as const,
    contractorsMissingRates: (orgId?: string, monthStr?: string) => ['contractors-missing-rates', orgId, monthStr] as const,
    weeklyContractorBreakdown: (orgId?: string, weekOffset?: number) => ['weekly-contractor-breakdown', orgId, weekOffset] as const,
    monthlyContractorBreakdown: (orgId?: string, monthStr?: string) => ['monthly-contractor-breakdown', orgId, monthStr] as const,
    staleness: (orgId?: string) => ['everhour-staleness', orgId] as const,
  },

  hoursSchedule: {
    byClient: (clientId?: string) => ['client-hours-schedule', clientId] as const,
    forDate: (clientId?: string, date?: string) => ['client-schedule-for-date', clientId, date] as const,
    allClients: (month?: string) => ['all-clients-schedule', month] as const,
  },

  accountability: {
    all: ['accountability-tasks'] as const,
    pending: (forUserId?: string, daysAhead?: number) => ['accountability-tasks', 'pending', forUserId, daysAhead] as const,
    history: (clientId?: string, daysBack?: number, taskType?: string) =>
      ['accountability-tasks', 'history', clientId, daysBack, taskType] as const,
    clientSettings: (clientId: string) => ['client-accountability-settings', clientId] as const,
  },

  documentationQuality: {
    teamSummaryBase: ['doc-quality-team-summary'] as const,
    teamSummary: (orgId?: string, days?: number) => ['doc-quality-team-summary', orgId, days] as const,
    byPersonBase: ['doc-quality-by-person'] as const,
    byPerson: (orgId?: string, days?: number) => ['doc-quality-by-person', orgId, days] as const,
    byClientBase: ['doc-quality-by-client'] as const,
    byClient: (orgId?: string, days?: number) => ['doc-quality-by-client', orgId, days] as const,
    personMetrics: (orgId?: string, personId?: string, days?: number) =>
      ['doc-quality-person-metrics', orgId, personId, days] as const,
    evaluate: (orgId?: string) => ['doc-quality-evaluate', orgId] as const,
    spotlights: (orgId?: string, minScore?: number, limit?: number) =>
      ['doc-quality-spotlights', orgId, minScore, limit] as const,
  },

  emailTemplates: {
    all: ['email-templates'] as const,
    byTypeAndOrg: (templateType?: string, organizationId?: string) =>
      ['email-template', templateType, organizationId] as const,
  },

  user: {
    profile: (userId?: string) => ['profile', userId] as const,
    preferences: ['user-preferences'] as const,
    twoFactorStatus: ['2fa-status'] as const,
  },

  ai: {
    prompts: ['ai-prompts'] as const,
    prompt: (id: string) => ['ai-prompt', id] as const,
    versions: (promptId: string) => ['ai-prompt-versions', promptId] as const,
    feedbackReviews: (orgId?: string, filters?: Record<string, unknown>) =>
      ['ai-feedback-review', orgId, filters] as const,
  },

  bdScoring: {
    settings: (orgId?: string) => ['bd-scoring-settings', orgId] as const,
  },

  businessDev: {
    dashboard: ['bd-dashboard'] as const,
    apiKeys: ['bd-api-keys'] as const,
    jobBids: ['job-bids'] as const,
    jobBidsList: (params: Record<string, unknown>) => ['job-bids', params] as const,
    jobBid: (bidId?: string) => ['job-bid', bidId] as const,
    bidStatusHistory: (bidId?: string) => ['bid-status-history', bidId] as const,
    teamActivity: (limit?: number) => ['team-activity', limit] as const,
    proposalTemplates: ['proposal-templates'] as const,
    jobPresence: ['job-presence'] as const,
  },

  upwork: {
    notifications: ['upwork-notifications'] as const,
    interviewInvites: ['upwork-interview-invites'] as const,
  },

  hubspot: {
    status: ['hubspot-status'] as const,
    company: (companyId?: string) => ['hubspot-company', companyId] as const,
  },

  scoreboard: {
    participants: ['scoreboard-participants'] as const,
    participantsByOrg: (organizationId?: string) => ['scoreboard-participants', organizationId] as const,
    orgMembersWithKeys: (organizationId?: string) => ['org-members-with-keys', organizationId] as const,
  },

  retainer: {
    byClient: (clientId?: string) => ['retainer-utilization', clientId] as const,
    allByOrg: (organizationId?: string) => ['retainer-utilization', 'all', organizationId] as const,
    alerts: (organizationId?: string) => ['retainer-alerts', organizationId] as const,
    thresholdsCheck: ['utilization-thresholds-check'] as const,
  },

  qbo: {
    all: ['qbo'] as const,
    status: () => ['qbo', 'status'] as const,
    accounts: () => ['qbo', 'accounts'] as const,
  },

  alerts: {
    all: (orgId: string) => ['alerts', orgId] as const,
    counts: (orgId: string) => ['alert-counts', orgId] as const,
    subscription: (orgId: string) => ['alerts-subscription', orgId] as const,
  },

  sla: {
    all: ['sla-tracking'] as const,
    config: (orgId?: string) => ['sla-config', orgId] as const,
    effectiveConfig: (clientId?: string) => ['sla-config', undefined, 'effective', clientId] as const,
    summary: (orgId?: string) => ['sla-summary', orgId] as const,
    active: (orgId?: string) => ['sla-active', orgId] as const,
    byClient: (clientId?: string) => ['sla-tracking', 'client', clientId] as const,
    byType: (slaType?: string) => ['sla-tracking', 'type', slaType] as const,
    clientOverrides: (clientId?: string) => ['sla-client-overrides', clientId] as const,
    allOverrides: (orgId?: string) => ['sla-all-overrides', orgId] as const,
    detail: (slaId?: string) => ['sla-detail', slaId] as const,
    history: (orgId?: string, filters?: Record<string, unknown>) => ['sla-history', orgId, filters] as const,
  },

  audit: {
    log: (orgId?: string, filters?: Record<string, unknown>, limit?: number) =>
      ['audit-log', orgId, filters, limit] as const,
    stats: (orgId?: string) => ['audit-log-stats', orgId] as const,
  },

  dashboard: {
    agencyInsights: ['agency-activity-insights'] as const,
    stats: ['dashboard-stats'] as const,
    recentActivity: ['recent-activity'] as const,
    activityFeed: (limit?: number) => ['activity-feed', limit] as const,
    recapCoverage: (orgId?: string) => ['recap-coverage', orgId] as const,
    recapCoverageSenders: (orgId?: string) => ['recap-coverage', orgId, 'senders'] as const,
    todaysFocus: (orgId?: string, viewingAsUserId?: string | null, assigneeEmail?: string | null) =>
      ['todays-focus', orgId, viewingAsUserId, assigneeEmail] as const,
    responseSpeedMetrics: (orgId?: string, viewingAsUserId?: string | null) =>
      ['response-speed-metrics', orgId, viewingAsUserId] as const,
    weeklyAccountabilityDigest: (
      orgId?: string,
      effectiveUserId?: string,
      personName?: string,
      isViewingAsOther?: boolean,
      clientsCount?: number,
      recapCoverageCount?: number,
    ) =>
      ['weekly-accountability-digest', orgId, effectiveUserId, personName, isViewingAsOther, clientsCount, recapCoverageCount] as const,
    standupAgenda: (orgId?: string, personId?: string, personName?: string) =>
      ['standup-agenda', orgId, personId, personName] as const,
    personalMeetingActionItems: (orgId?: string, personName?: string, days?: number) =>
      ['personal-meeting-action-items', orgId, personName, days] as const,
    goalsHealthWidgetData: ['goals-health-widget-data'] as const,
    goalsHealthWidgetDataByOrg: (orgId?: string) => ['goals-health-widget-data', orgId] as const,
    complianceWidgetData: ['compliance-widget-data'] as const,
    projectHealthWidgetData: ['project-health-widget-data'] as const,
    kpiClients: (orgId?: string) => ['dashboard-kpi-clients', orgId] as const,
    kpiProjects: (orgId?: string) => ['dashboard-kpi-projects', orgId] as const,
    kpiGoals: (orgId?: string) => ['dashboard-kpi-goals', orgId] as const,
  },

  questions: {
    byClient: (clientId?: string, status?: string) => ['client-questions', clientId, status] as const,
    stats: (clientId?: string) => ['client-question-stats', clientId] as const,
    detail: (id: string) => ['question', id] as const,
  },

  chat: {
    all: ['client-chats'] as const,
    sessions: (clientId?: string | null) => ['client-chats', clientId] as const,
    globalSessions: () => ['client-chats', null] as const,
    messages: (chatId?: string) => ['chat-messages', chatId] as const,
  },

  search: {
    universal: (query?: string, clientId?: string | null, limit?: number, useVectorSearch?: boolean) =>
      ['universal-search', query, clientId, limit, useVectorSearch] as const,
  },

  workspace: {
    pmSettings: ['workspace-pm-settings'] as const,
  },

  timeline: {
    unified: (clientId?: string, options?: Record<string, unknown>) =>
      ['unified-timeline', clientId, options] as const,
  },

  calendar: {
    all: ['calendar'] as const,
    eventsByClient: (clientId?: string) => ['calendar', 'events', clientId] as const,
    upcomingByClient: (clientId?: string) => ['calendar', 'events', clientId, 'upcoming'] as const,
    recentByClient: (clientId?: string) => ['calendar', 'recent', clientId] as const,
    syncStatus: (orgId?: string) => ['calendar', 'sync-status', orgId] as const,
    syncLogs: (orgId?: string, limit?: number) => ['calendar', 'sync-logs', orgId, limit] as const,
    meetingsNeedingTranscripts: (orgId?: string) =>
      ['calendar', 'meetings-needing-transcripts', orgId] as const,
    upcomingAll: (orgId?: string, leadTimeHours?: number) =>
      ['calendar', 'upcoming-all', orgId, leadTimeHours] as const,
  },

  celebrationSets: {
    all: ['celebration-sets'] as const,
    admin: (orgId?: string) => ['celebration-sets', 'admin', orgId] as const,
    enabled: (orgId?: string) => ['celebration-sets', 'enabled', orgId] as const,
  },

  integrations: {
    health: () => ['integration-health'] as const,
    syncActivity: (integration?: string) => ['integration-sync-activity', integration] as const,
    connectionDetails: (integration: string) => ['integration-connection', integration] as const,
    systemKeys: () => ['integration-system-keys'] as const,
  },

  oreja: {
    /** All Oreja data for an organization */
    all: (orgId: string) => ['oreja', orgId] as const,
    /** Sessions for a specific user */
    sessions: (orgId: string, userId: string) => ['oreja', orgId, 'sessions', userId] as const,
    /** Single session by ID */
    session: (sessionId: string) => ['oreja', 'session', sessionId] as const,
    /** Session history for an organization */
    history: (orgId: string) => ['oreja', orgId, 'history'] as const,
    /** Active session count for badge display */
    activeCount: (orgId: string, userId: string) => ['oreja', orgId, 'active-count', userId] as const,
  },
} as const;